"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowUpRight, Menu, X } from "@/components/Icons";
import BrandLockup from "@/components/BrandLockup";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/services", label: "Services" },
  { href: "/insights", label: "Insights" },
  { href: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  /* Hero is dark and full-height only on the homepage, so the
     transparent-over-hero treatment applies there. */
  const overHero = pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        overHero
          ? "bg-transparent"
          : "bg-[#1e3a14]/95 backdrop-blur-md shadow-lg shadow-black/10"
      }`}
    >
      <div className="mx-auto flex h-[114px] lg:h-[162px] max-w-[1320px] items-center justify-between px-5 sm:px-8 lg:px-12">
        <BrandLockup priority />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary navigation">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              aria-current={isActive(link.href) ? "page" : undefined}
              className={`relative px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 rounded-lg ${
                isActive(link.href)
                  ? "text-[#B3D670]"
                  : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              {link.label}
              {isActive(link.href) && (
                <span className="absolute bottom-0 left-4 right-4 h-px bg-[#B3D670]/60 rounded-full" />
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-[#B3D670] px-5 py-2.5 text-sm font-semibold text-[#1e3a14] transition-all duration-200 hover:bg-[#c8e98a] hover:scale-[1.02]"
          >
            Partner with us
            <ArrowUpRight className="size-4" />
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle navigation"
          aria-expanded={open}
          className="lg:hidden size-10 flex items-center justify-center rounded-lg text-white hover:bg-white/10 transition-colors"
        >
          {open ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden bg-[#1e3a14] transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[380px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="px-5 pb-5 sm:px-8" aria-label="Mobile navigation">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={`flex items-center justify-between border-b border-white/10 py-3.5 text-[15px] font-medium last:border-b-0 ${
                isActive(link.href) ? "text-[#B3D670]" : "text-white/70"
              }`}
            >
              {link.label}
              {isActive(link.href) && <span className="size-1.5 rounded-full bg-[#B3D670]" />}
            </Link>
          ))}

          <Link
            href="/contact"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#B3D670] px-5 py-3.5 text-sm font-bold text-[#1e3a14]"
          >
            Partner with us
            <ArrowUpRight className="size-4" />
          </Link>
        </nav>
      </div>
    </header>
  );
}
